import { useEffect, useMemo, useState } from "react";
import { api, API, formatApiError } from "@/lib/api";
import { PageHeader, TableShell } from "@/components/EntityManager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Printer, Download, MessageCircle, StickyNote } from "lucide-react";
import { inr, fmtDate, today } from "@/lib/format";
import { printCustomerLedger, printNoteVoucher } from "@/lib/print";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function CustomerLedger() {
  const [customers, setCustomers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [routeFrom, setRouteFrom] = useState("");
  const [routeDest, setRouteDest] = useState("");
  const [sel, setSel] = useState(new Set());
  const [codeQ, setCodeQ] = useState("");

  useEffect(() => {
    Promise.all([api.get("/customers"), api.get("/invoices"), api.get("/receipts")])
      .then(([c, i, r]) => {
        setCustomers(c.data || []);
        setInvoices(i.data || []);
        setReceipts(r.data || []);
        const pre = new URLSearchParams(window.location.search).get("customer");
        if (pre && (c.data || []).some((x) => x.id === pre)) setCustomerId(pre);
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  const isAll = customerId === "__ALL__";
  const customer = isAll ? { id: "__ALL__", name: "All Customers" } : customers.find((c) => c.id === customerId);
  useEffect(() => {
    const c = customers.find((x) => x.id === customerId);
    setCodeQ(c?.customer_code ? String(c.customer_code).toUpperCase() : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [payScore, setPayScore] = useState(null);
  const [notesOpen, setNotesOpen] = useState(false);
  const [resubmitId, setResubmitId] = useState("");
  const [resubmitAmt, setResubmitAmt] = useState("");
  const [resubmitReason, setResubmitReason] = useState("");
  const [resubmitting, setResubmitting] = useState(false);
  const resubmitNote = async (i) => {
    setResubmitting(true);
    try {
      const r = await api.post("/notes/resubmit", { kind: "debit", id: i.id, amount: Number(resubmitAmt) || 0, reason: resubmitReason });
      const amt = Number(resubmitAmt) || 0;
      if (r.data.status === "Pending") {
        toast.warning(`Debit Note ${inr(amt)} resubmitted — awaiting admin approval`, {
          description: "Alert the admin now?",
          duration: 15000,
          action: {
            label: "WhatsApp Admin",
            onClick: async () => {
              let phone = "";
              try { phone = (await api.get("/notes/admin-alert-contact")).data.phone || ""; } catch {}
              const d = String(phone).replace(/\D/g, "");
              const amsg = [
                "*NTC — Note Resubmitted for Approval*",
                "",
                `Debit Note ${inr(amt)} resubmitted on Bill ${String(i.invoice_no || "").replace(/^INV[-\s]?/i, "")}`,
                `Customer: ${i.customer_name || "—"}`,
                ...(resubmitReason ? [`Reason: ${resubmitReason}`] : []),
                "",
                "Please Approve/Reject in Outstanding → Approve Notes.",
              ].join("\n");
              window.open(d ? `https://wa.me/91${d.slice(-10)}?text=${encodeURIComponent(amsg)}` : `https://wa.me/?text=${encodeURIComponent(amsg)}`, "_blank");
            },
          },
        });
      } else {
        toast.success(`Debit Note ${inr(amt)} resubmitted — approved`);
      }
      setResubmitId("");
      setNotesOpen(false);
      api.get("/invoices").then((res) => setInvoices(res.data || [])).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resubmit failed");
    } finally {
      setResubmitting(false);
    }
  };
  const dnNotes = useMemo(() => {
    if (!customer || isAll) return [];
    return invoices
      .filter((i) => i.customer_id === customer.id || (i.customer_name && i.customer_name === customer.name))
      .filter((i) => Number(i.debit_note) > 0)
      .sort((a, b) => String(a.issue_date || "").localeCompare(String(b.issue_date || "")));
  }, [invoices, customer, isAll]);
  useEffect(() => {
    if (!customerId || customerId === "__ALL__") return setPayScore(null);
    api.get("/reports/payment-score", { params: { customer_id: customerId } }).then((r) => setPayScore(r.data)).catch(() => setPayScore(null));
  }, [customerId]);

  const { opening, entries, totals } = useMemo(() => {
    if (!customer) return { opening: 0, entries: [], totals: { debit: 0, credit: 0, closing: 0 } };
    const match = (x) => isAll || x.customer_id === customer.id || (x.customer_name && x.customer_name === customer.name);
    const normBill = (v) => String(v || "").replace(/^INV[-\s]?/i, "").replace(/^0+/, "").trim();
    const billedIds = new Set();
    const billedNos = new Set();
    receipts.forEach((r) => {
      if (r.reversed) return;
      if (r.against_invoice_id) billedIds.add(r.against_invoice_id);
      if (r.against_invoice_no) billedNos.add(normBill(r.against_invoice_no));
      (r.bills || []).forEach((b) => b.bill_no && billedNos.add(normBill(b.bill_no)));
    });
    const hasMr = (i) => billedIds.has(i.id) || billedNos.has(normBill(i.invoice_no));
    const invById = {};
    const invByNo = {};
    invoices.forEach((i) => {
      invById[i.id] = i;
      const n = normBill(i.invoice_no);
      if (n) invByNo[n] = i;
    });
    const all = [
      ...invoices.filter(match).filter((i) => isAdmin || !hasMr(i)).map((i) => ({
        date: i.issue_date || String(i.created_at || "").slice(0, 10),
        cname: i.customer_name || "—",
        particulars: isAll ? `${i.customer_name || "—"} — Bill Raised` : "Bill Raised",
        bill_no: String(i.invoice_no || "").replace(/^INV[-\s]?/i, ""),
        bill_date: i.issue_date || "",
        branch: i.office || "Chennai",
        from_place: i.from_place || "",
        destination: i.destination || "",
        bill_copy_path: i.bill_copy_path || "",
        debit: Number(i.total) || 0,
        credit: 0,
        credit_bill_no: "",
        credit_bill_date: "",
      })),
      ...(isAdmin ? receipts.filter(match) : []).map((r) => {
        const dedOk = (r.deduction_status || "") !== "Rejected";
        const bills = r.bills || [];
        const tds = bills.length ? bills.reduce((s, b) => s + (Number(b.tds) || 0), 0) : Number(r.tds) || 0;
        const ded = bills.length ? bills.reduce((s, b) => s + (Number(b.deduction) || 0), 0) : Number(r.deduction) || 0;
        const credit = r.reversed
          ? 0
          : bills.length
          ? bills.reduce((s, b) => s + (Number(b.amount) || 0) - (dedOk ? 0 : Number(b.deduction) || 0), 0)
          : (Number(r.amount) || 0) + tds + (dedOk ? ded : 0);
        const settle = [tds > 0 ? `TDS ${inr(tds)}` : "", ded > 0 && dedOk ? `Deduction ${inr(ded)}` : ""].filter(Boolean).join(" + ");
        const linked = bills.length
          ? bills.map((b) => invByNo[normBill(b.bill_no)]).filter(Boolean)
          : [invById[r.against_invoice_id] || invByNo[normBill(r.against_invoice_no)]].filter(Boolean);
        const creditBillNo = bills.length
          ? bills.map((b) => normBill(b.bill_no)).filter(Boolean).join(", ")
          : normBill(r.against_invoice_no) || (linked[0] ? normBill(linked[0].invoice_no) : "");
        const mrNo = String(r.mr_no || "").replace(/^MR[-\s]?/i, "");
        return {
          date: r.date || String(r.created_at || "").slice(0, 10),
          cname: r.customer_name || "—",
          particulars: (isAll ? `${r.customer_name || "—"} — ` : "") + (r.reversed
            ? `Payment Reversed — cheque bounced${r.cheque_no ? ` (Cheque ${r.cheque_no})` : ""}`
            : `Payment Received${r.payment_mode ? ` (${r.payment_mode})` : ""}${mrNo ? ` — MR ${mrNo}` : ""}${settle ? ` — incl. ${settle}` : ""}`),
          bill_no: "",
          bill_date: "",
          branch: r.office || "Chennai",
          from_place: linked.length === 1 ? linked[0].from_place || "" : "",
          destination: linked.length === 1 ? linked[0].destination || "" : "",
          debit: 0,
          credit,
          credit_bill_no: creditBillNo || mrNo,
          credit_bill_date: linked[0]?.issue_date || r.bill_date || "",
          advice_path: r.advice_path || "",
        };
      }),
      ...receipts
        .filter((r) => match(r) && r.cheque_status === "Bounced" && Number(r.bounce_charge) > 0)
        .map((r) => ({
          date: r.deposit_date || r.cheque_date || r.date || String(r.created_at || "").slice(0, 10),
          cname: r.customer_name || "—",
          particulars: `${isAll ? `${r.customer_name || "—"} — ` : ""}Cheque Bounce Charges (Cheque ${r.cheque_no || "—"})`,
          bill_no: "",
          bill_date: "",
          branch: r.office || "Chennai",
          debit: Number(r.bounce_charge) || 0,
          credit: 0,
          credit_bill_no: String(r.mr_no || "").replace(/^MR[-\s]?/i, ""),
          credit_bill_date: "",
        })),
    ].sort((a, b) => String(a.date).localeCompare(String(b.date)));
    const opening = from ? all.filter((e) => e.date < from).reduce((s, e) => s + e.debit - e.credit, 0) : 0;
    const rf = routeFrom.trim().toLowerCase();
    const rd = routeDest.trim().toLowerCase();
    const matchRoute = (e) =>
      (!rf || (e.from_place || "").toLowerCase().includes(rf)) && (!rd || (e.destination || "").toLowerCase().includes(rd));
    let bal = opening;
    const entries = all
      .filter((e) => (!from || e.date >= from) && (!to || e.date <= to) && matchRoute(e))
      .map((e) => ({ ...e, balance: (bal += e.debit - e.credit) }));
    const debit = entries.reduce((s, e) => s + e.debit, 0);
    const credit = entries.reduce((s, e) => s + e.credit, 0);
    return { opening, entries, totals: { debit, credit, closing: bal } };
  }, [customer, isAll, invoices, receipts, from, to, routeFrom, routeDest, isAdmin]);

  const shareWhatsApp = async () => {
    const digits = String(customer?.phone || "").replace(/\D/g, "");
    if (!digits) {
      toast.error("No phone number saved for this customer in Customer Master");
      return;
    }
    let ledgerLink = "";
    try {
      const res = await api.post("/ledger-share", { customer_id: customer.id, from_date: from, to_date: to });
      ledgerLink = `${window.location.origin}/api/public/customer-ledger/${res.data.id}`;
    } catch (e) { /* link optional */ }
    const period = `${from ? fmtDate(from) : "Start"} to ${to ? fmtDate(to) : "Today"}`;
    const waBranch = customer.branch || Array.from(new Set(entries.map((e) => e.branch).filter(Boolean))).join(", ");
    const headLines = `${customer.customer_code ? `Customer Code: ${customer.customer_code}\n` : ""}${waBranch ? `Branch: ${waBranch}\n` : ""}`;
    const lines = entries.slice(-10).map((e) => `${fmtDate(e.date)} — ${e.particulars}${(e.bill_no || e.credit_bill_no) ? ` (${e.bill_no || e.credit_bill_no})` : ""}: ${e.debit ? `Dr ${inr(e.debit)}` : `Cr ${inr(e.credit)}`}`);
    const msg = `Dear ${customer.name},\n\nYour account summary with Nisha Transport Corporation (${period}):\n${headLines}\n${from ? `Opening Balance: ${inr(opening)}\n` : ""}Total Billed: ${inr(totals.debit)}\nTotal Received: ${inr(totals.credit)}\n*Closing Balance: ${inr(totals.closing)}*\n\nRecent entries:\n${lines.join("\n")}\n${ledgerLink ? `\n📄 View / download full ledger (PDF):\n${ledgerLink}\n` : ""}\nKindly arrange payment of the balance amount at the earliest.\n\n— Nisha Transport Corporation`;
    window.open(`https://wa.me/${digits.length === 10 ? "91" + digits : digits}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const selectedEntries = entries.filter((_, i) => sel.has(i));
  const printEntries = selectedEntries.length ? selectedEntries : entries;
  const printTotals = selectedEntries.length
    ? {
        debit: selectedEntries.reduce((s, e) => s + e.debit, 0),
        credit: selectedEntries.reduce((s, e) => s + e.credit, 0),
        closing: selectedEntries[selectedEntries.length - 1].balance,
      }
    : totals;
  const allSel = entries.length > 0 && entries.every((_, i) => sel.has(i));
  const toggleAll = () => setSel(allSel ? new Set() : new Set(entries.map((_, i) => i)));
  const toggleOne = (i) => setSel((s) => { const n = new Set(s); n.has(i) ? n.delete(i) : n.add(i); return n; });

  const custTotals = isAll && entries.length
    ? Object.values(
        entries.reduce((acc, e) => {
          const k = e.cname || "—";
          const g = (acc[k] = acc[k] || { cname: k, debit: 0, credit: 0 });
          g.debit += e.debit;
          g.credit += e.credit;
          return acc;
        }, {})
      )
        .map((g) => ({ ...g, balance: g.debit - g.credit }))
        .sort((a, b) => b.balance - a.balance)
    : [];

  const exportCsv = () => {
    const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const rows = printEntries;
    const csvBranch = customer.branch || Array.from(new Set(rows.map((e) => e.branch).filter(Boolean))).join(" / ");
    const lines = [`Customer Name,${cell(customer.name)}`, `Customer Code,${cell(customer.customer_code || "—")}`, `Branch,${cell(csvBranch || "—")}`, `Period,${from || "Start"} to ${to || "Today"}`, "", "Branch,Date,Particulars,Bill No.,Bill Date,From,Destination,Debit (Bill),Credit Bill,Credit Bill Date,Credit Amount,Balance Amount"];
    if (from && !selectedEntries.length) lines.push(`,,Opening Balance,,,,,,,,,"${opening}"`);
    rows.forEach((e) => lines.push([e.branch, e.date, e.particulars, e.bill_no, e.bill_date, e.from_place || "", e.destination || "", e.debit || "", e.credit_bill_no, e.credit_bill_date, e.credit || "", e.balance].map(cell).join(",")));
    lines.push(`,,TOTAL,,,,,"${printTotals.debit}",,,"${printTotals.credit}","${printTotals.closing}"`);
    if (custTotals.length) {
      lines.push("", "Customer-wise Totals", "Customer,Billed (Debit),Received (Credit),Balance Amount");
      custTotals.forEach((g) => lines.push([g.cname, g.debit, g.credit, g.balance].map(cell).join(",")));
      lines.push(`TOTAL,"${totals.debit}","${totals.credit}","${totals.closing}"`);
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `customer-ledger-${customer.name.replace(/\s+/g, "-").toLowerCase()}-${today()}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <Dialog open={notesOpen} onOpenChange={setNotesOpen}>
        <DialogContent className="sm:max-w-[720px]" data-testid="customer-notes-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Debit Note History — {customer?.name}</DialogTitle>
          </DialogHeader>
          {(() => {
            let run = 0;
            return (
              <div className="max-h-[60vh] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 hover:bg-slate-50">
                      {["Date", "Bill No.", "Reason", "Amount", "Running Total", ""].map((h) => (
                        <TableHead key={h} className={`text-[11px] uppercase tracking-[0.15em] text-slate-500 ${h === "Amount" || h === "Running Total" ? "text-right" : ""}`}>{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dnNotes.map((i) => {
                      const amt = Number(i.debit_note) || 0;
                      const counted = !i.debit_note_status || i.debit_note_status === "Approved";
                      if (counted) run += amt;
                      const editing = resubmitId === i.id;
                      return (
                        <TableRow key={i.id} data-testid="customer-note-row">
                          <TableCell className="text-sm whitespace-nowrap">{i.issue_date ? fmtDate(i.issue_date) : "—"}</TableCell>
                          <TableCell className="text-sm">{String(i.invoice_no || "—").replace(/^INV[-\s]?/i, "")}</TableCell>
                          <TableCell className="text-sm">
                            {editing ? (
                              <Input className="h-7 w-[170px] text-xs" placeholder="Reason" value={resubmitReason} onChange={(e) => setResubmitReason(e.target.value)} data-testid="resubmit-reason-input" />
                            ) : (
                              <>
                                {i.debit_note_reason || "—"}
                                {i.debit_note_status === "Pending" && <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">PENDING</span>}
                                {i.debit_note_status === "Rejected" && (
                                  <span className="ml-1.5 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500">REJECTED{i.debit_note_reject_reason ? ` — ${i.debit_note_reject_reason}` : ""}</span>
                                )}
                              </>
                            )}
                          </TableCell>
                          <TableCell className="text-right text-sm tabular-nums text-red-600">
                            {editing ? (
                              <Input type="number" className="h-7 w-[90px] text-right text-xs" value={resubmitAmt} onChange={(e) => setResubmitAmt(e.target.value)} data-testid="resubmit-amount-input" />
                            ) : (
                              inr(amt)
                            )}
                          </TableCell>
                          <TableCell className="text-right text-sm font-semibold tabular-nums">{counted ? inr(run) : "—"}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              {editing ? (
                                <>
                                  <Button size="sm" className="h-7 bg-amber-600 px-2.5 text-xs text-white hover:bg-amber-700" disabled={resubmitting || !Number(resubmitAmt)} onClick={() => resubmitNote(i)} data-testid="confirm-resubmit-btn">
                                    {resubmitting ? "…" : "Resubmit"}
                                  </Button>
                                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setResubmitId("")} data-testid="cancel-resubmit-btn">✕</Button>
                                </>
                              ) : (
                                <>
                                  {i.debit_note_status === "Rejected" && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-7 border-amber-300 px-2 text-xs text-amber-700 hover:bg-amber-50"
                                      title="Fix & resubmit this rejected note for approval"
                                      onClick={() => { setResubmitId(i.id); setResubmitAmt(String(amt)); setResubmitReason(i.debit_note_reason || ""); }}
                                      data-testid="resubmit-note-btn"
                                    >
                                      Resubmit
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-red-600"
                                    title="Print A5 Debit Note voucher"
                                    onClick={() => printNoteVoucher({ date: i.issue_date, party: i.customer_name, ref: i.invoice_no, amount: amt, reason: i.debit_note_reason, branch: i.office }, "debit")}
                                    data-testid="customer-note-voucher-btn"
                                  >
                                    <Printer className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow className="bg-slate-50 font-bold hover:bg-slate-50">
                      <TableCell colSpan={3}>TOTAL APPROVED DEBIT NOTES</TableCell>
                      <TableCell className="text-right tabular-nums text-red-600" data-testid="customer-notes-total">{inr(run)}</TableCell>
                      <TableCell colSpan={2} />
                    </TableRow>
                  </TableBody>
                </Table>
                <p className="mt-2 text-xs text-muted-foreground">Every debit note on this customer's bills, oldest first, with a running total — for quick dispute settlement.</p>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <PageHeader
        title="Customer Ledger"
        subtitle="Bill-wise debits and payment credits with running balance for any customer."
        action={
          <div className="flex items-center gap-2">
            {dnNotes.length > 0 && (
              <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" onClick={() => setNotesOpen(true)} data-testid="customer-notes-btn">
                <StickyNote className="mr-2 h-4 w-4" /> Note History ({dnNotes.length})
              </Button>
            )}
            <Button variant="outline" className="text-emerald-600" onClick={shareWhatsApp} disabled={!customer || !entries.length || isAll} data-testid="customer-ledger-whatsapp-btn">
              <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp
            </Button>
            <Button variant="outline" onClick={exportCsv} disabled={!customer || !entries.length} data-testid="customer-ledger-excel-btn">
              <Download className="mr-2 h-4 w-4" /> Excel
            </Button>
            <Button onClick={() => printCustomerLedger({ customer, from, to, opening: selectedEntries.length ? null : opening, entries: printEntries, totals: printTotals, custTotals })} disabled={!customer || !entries.length} data-testid="customer-ledger-print-btn">
              <Printer className="mr-2 h-4 w-4" /> Print{sel.size ? ` (${selectedEntries.length})` : ""}
            </Button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-lg border border-border bg-slate-50 px-4 py-3" data-testid="customer-ledger-bar">
        <div className="grid gap-1">
          <Label className="text-xs">Customer Name</Label>
          <Select value={customerId} onValueChange={(v) => { setCustomerId(v); setSel(new Set()); }}>
            <SelectTrigger className="h-9 w-[280px] bg-white text-sm" data-testid="customer-ledger-select">
              <SelectValue placeholder="Select customer" />
            </SelectTrigger>
            <SelectContent className="max-h-[320px]">
              <SelectItem value="__ALL__" data-testid="customer-ledger-all-option">All Customers</SelectItem>
              {customers.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.customer_code ? `${c.customer_code} — ${c.name}` : c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1">
          <Label className="text-xs">Customer Code</Label>
          <Input
            className="h-9 w-[120px] bg-white font-semibold uppercase"
            placeholder="Type code"
            value={codeQ}
            onChange={(e) => {
              const v = e.target.value.toUpperCase();
              setCodeQ(v);
              const m = v.trim() && customers.find((c) => String(c.customer_code || "").trim().toUpperCase() === v.trim());
              if (m) { setCustomerId(m.id); setSel(new Set()); }
            }}
            data-testid="customer-ledger-code"
          />
        </div>
        <div className="grid gap-1">
          <Label className="text-xs">Branch</Label>
          <Input readOnly className="h-9 w-[130px] bg-white font-semibold" value={customer?.branch || (!isAll && entries.length ? Array.from(new Set(entries.map((e) => e.branch).filter(Boolean))).join(", ") : "") || "—"} data-testid="customer-ledger-branch" />
        </div>
        {payScore && (
          <span
            className={`mb-1.5 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold text-white ${payScore.score === "A" ? "bg-emerald-600" : payScore.score === "B" ? "bg-amber-600" : "bg-red-600"}`}
            title={`Payment Score ${payScore.score} — ${payScore.reason}`}
            data-testid="ledger-payment-score-badge"
          >
            Score {payScore.score}
            <span className="font-normal opacity-90">· {payScore.reason}</span>
          </span>
        )}
        <div className="grid gap-1">
          <Label className="text-xs">Date From</Label>
          <Input type="date" className="h-9 w-[150px] bg-white" value={from} onChange={(e) => { setFrom(e.target.value); setSel(new Set()); }} data-testid="customer-ledger-from" />
        </div>
        <div className="grid gap-1">
          <Label className="text-xs">To</Label>
          <Input type="date" className="h-9 w-[150px] bg-white" value={to} onChange={(e) => { setTo(e.target.value); setSel(new Set()); }} data-testid="customer-ledger-to" />
        </div>
        <div className="grid gap-1">
          <Label className="text-xs">From (Route)</Label>
          <Input className="h-9 w-[140px] bg-white" placeholder="e.g. Chennai" value={routeFrom} onChange={(e) => { setRouteFrom(e.target.value); setSel(new Set()); }} data-testid="customer-ledger-route-from" />
        </div>
        <div className="grid gap-1">
          <Label className="text-xs">Destination</Label>
          <Input className="h-9 w-[140px] bg-white" placeholder="e.g. Jaipur" value={routeDest} onChange={(e) => { setRouteDest(e.target.value); setSel(new Set()); }} data-testid="customer-ledger-route-dest" />
        </div>
        {(from || to || routeFrom || routeDest) && (
          <Button variant="ghost" size="sm" className="h-9 text-xs" onClick={() => { setFrom(""); setTo(""); setRouteFrom(""); setRouteDest(""); }} data-testid="customer-ledger-clear">
            Clear
          </Button>
        )}
        {customer && (
          <span className="pb-2 text-xs text-muted-foreground" data-testid="customer-ledger-summary">
            {entries.length} entries · Closing Balance: <b className={totals.closing > 0 ? "text-red-600" : "text-emerald-700"}>{inr(totals.closing)}</b>
          </span>
        )}
      </div>

      <TableShell>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="w-[36px]">
                <Checkbox checked={allSel} onCheckedChange={toggleAll} disabled={!entries.length} data-testid="ledger-select-all" />
              </TableHead>
              {["Branch", "Date", "Particulars", "Bill No.", "Bill Date", "From", "Destination", "Debit (Bill)", "Credit Bill", "Credit Bill Date", "Credit Amount", "Balance Amount"].map((h, i) => (
                <TableHead key={h} className={`text-[11px] uppercase tracking-[0.1em] text-slate-500 ${[7, 10, 11].includes(i) ? "text-right" : ""}`}>{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {!customer && (
              <TableRow>
                <TableCell colSpan={13} className="py-12 text-center text-muted-foreground">Select a customer to view their ledger.</TableCell>
              </TableRow>
            )}
            {customer && entries.length === 0 && (
              <TableRow>
                <TableCell colSpan={13} className="py-12 text-center text-muted-foreground">No bills or receipts for this customer in the selected period.</TableCell>
              </TableRow>
            )}
            {customer && from && entries.length > 0 && (
              <TableRow className="bg-slate-50/60 hover:bg-slate-50/60">
                <TableCell />
                <TableCell />
                <TableCell className="text-sm">{fmtDate(from)}</TableCell>
                <TableCell className="text-sm font-semibold" colSpan={9}>Opening Balance</TableCell>
                <TableCell className="text-right text-sm font-semibold tabular-nums">{inr(opening)}</TableCell>
              </TableRow>
            )}
            {customer &&
              entries.map((e, i) => (
                <TableRow key={i} data-testid="customer-ledger-row">
                  <TableCell>
                    <Checkbox checked={sel.has(i)} onCheckedChange={() => toggleOne(i)} data-testid="ledger-select-row" />
                  </TableCell>
                  <TableCell className="text-sm">{e.branch || "—"}</TableCell>
                  <TableCell className="text-sm">{fmtDate(e.date)}</TableCell>
                  <TableCell className="text-sm">{e.particulars}</TableCell>
                  <TableCell className="text-sm">
                    {e.bill_no || "—"}
                    {e.bill_copy_path ? (
                      <a
                        href={`${API}/files/${e.bill_copy_path}?auth=${localStorage.getItem("ntc_token")}`}
                        target="_blank"
                        rel="noreferrer"
                        className="ml-2 text-xs font-medium text-emerald-600 hover:underline"
                        title="View uploaded bill copy"
                        data-testid="ledger-bill-copy-link"
                      >
                        View
                      </a>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-sm">{e.bill_date ? fmtDate(e.bill_date) : "—"}</TableCell>
                  <TableCell className="text-sm" data-testid="ledger-from-cell">{e.from_place || "—"}</TableCell>
                  <TableCell className="text-sm" data-testid="ledger-destination-cell">{e.destination || "—"}</TableCell>
                  <TableCell className="text-right text-sm tabular-nums">{e.debit ? inr(e.debit) : "—"}</TableCell>
                  <TableCell className="text-sm">
                    {e.credit ? e.credit_bill_no || "—" : "—"}
                    {e.credit && e.advice_path ? (
                      <a
                        href={`${API}/files/${e.advice_path}?auth=${localStorage.getItem("ntc_token")}`}
                        target="_blank"
                        rel="noreferrer"
                        className="ml-2 text-xs font-medium text-emerald-600 hover:underline"
                        title="View payment advice"
                        data-testid="ledger-advice-link"
                      >
                        Advice
                      </a>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-sm">{e.credit && e.credit_bill_date ? fmtDate(e.credit_bill_date) : "—"}</TableCell>
                  <TableCell className="text-right text-sm tabular-nums text-emerald-700">{e.credit ? inr(e.credit) : "—"}</TableCell>
                  <TableCell className="text-right text-sm font-medium tabular-nums">{inr(e.balance)}</TableCell>
                </TableRow>
              ))}
            {customer && entries.length > 0 && (
              <TableRow className="bg-slate-50 font-bold hover:bg-slate-50">
                <TableCell colSpan={8}>TOTAL</TableCell>
                <TableCell className="text-right tabular-nums">{inr(totals.debit)}</TableCell>
                <TableCell colSpan={2} />
                <TableCell className="text-right tabular-nums text-emerald-700">{inr(totals.credit)}</TableCell>
                <TableCell className={`text-right tabular-nums ${totals.closing > 0 ? "text-red-600" : ""}`}>{inr(totals.closing)}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableShell>
      {isAll && entries.length > 0 && (
        <div className="mt-6" data-testid="all-ledger-customer-totals">
          <h3 className="mb-2 font-display text-lg font-semibold">Customer-wise Totals</h3>
          <TableShell>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  {["Customer", "Billed (Debit)", "Received (Credit)", "Balance Amount"].map((h, i) => (
                    <TableHead key={h} className={`text-[11px] uppercase tracking-[0.1em] text-slate-500 ${i > 0 ? "text-right" : ""}`}>{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {custTotals.map((g) => (
                    <TableRow key={g.cname} data-testid="all-ledger-total-row">
                      <TableCell className="text-sm font-semibold">{g.cname}</TableCell>
                      <TableCell className="text-right text-sm tabular-nums">{inr(g.debit)}</TableCell>
                      <TableCell className="text-right text-sm tabular-nums text-emerald-700">{inr(g.credit)}</TableCell>
                      <TableCell className={`text-right text-sm font-bold tabular-nums ${g.balance > 0.01 ? "text-red-600" : "text-emerald-700"}`}>{inr(g.balance)}</TableCell>
                    </TableRow>
                  ))}
                <TableRow className="bg-slate-100 font-bold">
                  <TableCell>TOTAL</TableCell>
                  <TableCell className="text-right tabular-nums">{inr(totals.debit)}</TableCell>
                  <TableCell className="text-right tabular-nums text-emerald-700">{inr(totals.credit)}</TableCell>
                  <TableCell className={`text-right tabular-nums ${totals.closing > 0 ? "text-red-600" : ""}`}>{inr(totals.closing)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableShell>
        </div>
      )}
    </div>
  );
}
